import React, { useEffect, useMemo, useRef, useState } from "react"
import {
	GlobalSidebar,
	SidebarList,
	FormSwitchInput,
	FormInputAntd,
	FormSelectAntd,
	AntdButton,
	AntdInput,
} from "components"
import { yupResolver } from "@hookform/resolvers/yup"
import { SDSuppliersInterface } from "types/staticData.types"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { SDSuppliersInitialValues, SDSuppliersSchema } from "Schema/StaticData/SDSuppliers"
import {
	filteredSDSupplierListBySearch,
	getSDSuppliers,
	isSDSuppliersLoading,
	isSDSuppliersSaving,
	saveSDSuppliers,
	selectSDSuppliersById,
} from "store/StaticData/SDSuppliers.slice"
import { useAppDispatch, useAppSelector } from "hooks"
import { selectSDCompaniesDropdownList } from "store/StaticData/SDCompanies.slice"
import { selectCurrentUser } from "store/auth.slice"
import { usePBcodes } from "../../hooks/usePBcodes"
import { PBCodeOptions } from "../../config/constants"
import { SearchOutlined } from "@ant-design/icons"
import { Divider } from "antd-v5"
import { useAdminQueryParams } from "hooks"
import { sortByProperty } from "config/utils"

interface EquipmentTypeFormHandler extends SDSuppliersInterface {}

const SDSuppliers = () => {
	const [search, setSearch] = useState<string>("")

	const dispatch = useAppDispatch()
	const saving = useAppSelector(isSDSuppliersSaving())
	const loading = useAppSelector(isSDSuppliersLoading())
	const { params, setSearchParams } = useAdminQueryParams()
	const { selected: selectedSupplier = "" } = params
	const SDSuppliers = useAppSelector(selectSDSuppliersById(selectedSupplier))
	const allCompanies = useAppSelector(selectSDCompaniesDropdownList) || []
	const filteredSDSupplierSearch = useAppSelector(filteredSDSupplierListBySearch(search, ["name"]))
	const isNewBeingCreated = useRef(false)
	const user = useAppSelector(selectCurrentUser())
	const { fetchPbCodesByUsedForFilter, isLoading: isPbCodesLoading, pbCodes } = usePBcodes()
	const methods = useForm<EquipmentTypeFormHandler>({ resolver: yupResolver(SDSuppliersSchema) })
	const {
		handleSubmit,
		reset,
		setValue,
		watch,
		formState: { errors, isSubmitted, isSubmitting, isDirty },
	} = methods

	const sidebarSDSuppliers = useMemo(() => {
		return (filteredSDSupplierSearch ?? [])?.map(SDSuppliers => ({
			_id: SDSuppliers._id,
			name: SDSuppliers.name,
		}))
	}, [saving, filteredSDSupplierSearch?.length, isSubmitted, search])

	const onSubmit: SubmitHandler<EquipmentTypeFormHandler> = data => {
		let company
		const { payCode = null, ...rest } = data
		isNewBeingCreated.current = true

		if (user?.roles.filter(role => role.companyId).length === 1) {
			company = user?.roles[0]?.companyId ?? ""
		} else {
			company = data.company
		}

		const sanitizedSupplierData = {
			...rest,
			...(payCode && { payCode }),
		}

		dispatch(
			saveSDSuppliers(sanitizedSupplierData, company, id => {
				setSearchParams({ selected: id })
				reset({ ...sanitizedSupplierData })
			}),
		)
	}

	useEffect(() => {
		dispatch(getSDSuppliers(true))
		fetchPbCodesByUsedForFilter(PBCodeOptions.SUPPLIER)
	}, [])

	useEffect(() => {
		if (!selectedSupplier && !!filteredSDSupplierSearch?.length && !isNewBeingCreated.current) {
			setSearchParams({ selected: filteredSDSupplierSearch?.[0]?._id })
		}
	}, [params, filteredSDSupplierSearch])

	useEffect(() => {
		if (selectedSupplier && SDSuppliers) {
			reset({ ...SDSuppliersInitialValues, ...SDSuppliers })
		} else if (!selectedSupplier && !isNewBeingCreated.current) {
			reset({
				...SDSuppliersInitialValues,
				...(user?.roles.length ? { company: user.roles[0]?.companyId ?? allCompanies?.[1]?.value } : {}),
			})
		}
	}, [selectedSupplier, SDSuppliers])

	const handleSelected = (supplierId: string): void => {
		setSearchParams({ selected: supplierId })
	}

	return (
		<>
			<GlobalSidebar>
				<AntdInput
					onChange={e => setSearch(e.target.value)}
					placeholder="Search"
					type="text"
					suffix={<SearchOutlined size={14} style={{ color: "rgb(156 163 175)" }} />}
				/>
				<Divider className="bg-slate-700" style={{ margin: "15px 0 10px 0" }} />
				<SidebarList
					title={"Suppliers"}
					loading={loading}
					selected={selectedSupplier}
					setSelected={handleSelected}
					list={sidebarSDSuppliers.sort(sortByProperty("name"))}
				/>
			</GlobalSidebar>
			<FormProvider {...methods}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="flex flex-col gap-2">
						<div className="grid grid-cols-4 gap-x-3">
							{user?.isSuperAdmin ? (
								<FormSelectAntd
									className="col-span-1"
									disabled={!!selectedSupplier}
									value={selectedSupplier ? `${SDSuppliers?.company}` : watch("company")}
									label="Company"
									name="company"
									options={allCompanies ?? []}
								/>
							) : (
								<></>
							)}
							<FormInputAntd className="col-span-1" type="text" label="Supplier Name" name="name" />
							<FormSelectAntd
								disabled={!pbCodes.length}
								label="PB Code"
								name="payCode"
								options={[
									{ label: "Select PbCode", value: "" },
									...pbCodes.map(({ _id, name }) => ({ label: name, value: _id })),
								]}
							/>
						</div>
						<FormSwitchInput
							containerClass="flex  items-center w-40"
							label="Enabled"
							name="isEnabled"
							watch={watch}
							errors={errors}
							setValue={setValue}
						/>

						<div className="flex gap-x-2">
							<AntdButton
								onClick={() => {
									isNewBeingCreated.current = true
									reset({ ...SDSuppliersInitialValues })
									setSearchParams({ selected: "" })
								}}
							>
								New
							</AntdButton>
							<AntdButton
								disabled={!isDirty || isSubmitting || isPbCodesLoading}
								loading={saving || isPbCodesLoading}
								htmlType="submit"
							>
								{selectedSupplier ? "Update" : "Save"}
							</AntdButton>
						</div>
					</div>
				</form>
			</FormProvider>
		</>
	)
}

export default SDSuppliers
